import { fetchPrimary } from "../environments/primary/fetch";
import type { CanvasShareDependency } from "./canvasShareWords";

// W-7cdf5337: what an approval of Cleo's Canvas share reads from the World,
// under the person's own grant and through the Desk's Canvas door, so it can
// say what the share gives before the person decides: the Components and
// data it carries (the same canvas-dependencies preview the Desk's Share
// dialog lists and the World issues the grants from), and the group it names.
// Never from Cleo's words. Each read answers; none throws.

/** Not read yet, read, or refused with the reason the person is shown. */
export type CanvasShareDataRead =
  | { readonly status: "reading" }
  | {
      readonly status: "loaded";
      readonly digest: string;
      readonly dependencies: ReadonlyArray<CanvasShareDependency>;
    }
  | { readonly status: "failed"; readonly reason: string };

/** The exact preview a share with data names: its Canvas, revision and whether it gives changes. */
export interface CanvasShareDataRef {
  readonly canvasId: string;
  readonly sequence: number;
  readonly dataChanges: boolean;
  readonly digest: string;
}

const READ_TIMEOUT_MS = 15_000;
export const CANVAS_SHARE_DATA_UNREAD =
  "Neucleos could not list the Components and data this share gives, so it cannot be approved here.";

export async function canvasDoor(
  worldKey: string,
  action: string,
  args: Record<string, unknown>,
): Promise<
  | { readonly ok: true; readonly body: Record<string, unknown> }
  | { readonly ok: false; readonly reason: string | null }
> {
  try {
    const response = await fetchPrimary(`/api/neucleos/canvas/${action}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ worldKey, ...args }),
      // A read that never answers is a refusal the person sees, not a card stuck reading.
      signal: AbortSignal.timeout(READ_TIMEOUT_MS),
    });
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (response.ok && body?.["ok"] === true && !body["gateRefusal"]) return { ok: true, body };
    const reason = body?.["reason"];
    return {
      ok: false,
      reason:
        typeof reason === "string" && reason !== ""
          ? reason.replace(/^REFUSED:\s*/, "").replace(/ Law: [\s\S]*$/, "")
          : null,
    };
  } catch {
    return { ok: false, reason: null };
  }
}

function dependencyOf(value: unknown): CanvasShareDependency | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  const actions = row["actions"];
  if (
    typeof row["kind"] !== "string" ||
    typeof row["id"] !== "string" ||
    typeof row["name"] !== "string" ||
    !Array.isArray(actions) ||
    !actions.every((action) => typeof action === "string") ||
    (row["version"] !== undefined && typeof row["version"] !== "number") ||
    (row["rowScope"] !== undefined && typeof row["rowScope"] !== "string")
  )
    return null;
  return {
    kind: row["kind"],
    id: row["id"],
    name: row["name"],
    actions: actions as string[],
    ...(typeof row["version"] === "number" ? { version: row["version"] } : {}),
    ...(typeof row["rowScope"] === "string" ? { rowScope: row["rowScope"] } : {}),
  };
}

/** The Components and data a share of this Canvas revision gives, as the World lists them now. */
export async function readCanvasShareData(
  worldKey: string,
  ref: Pick<CanvasShareDataRef, "canvasId" | "sequence" | "dataChanges">,
): Promise<CanvasShareDataRead> {
  const read = await canvasDoor(worldKey, "canvas-dependencies", {
    canvasId: ref.canvasId,
    sequence: ref.sequence,
    dataChanges: ref.dataChanges,
  });
  if (!read.ok)
    return {
      status: "failed",
      reason:
        read.reason === null
          ? CANVAS_SHARE_DATA_UNREAD
          : `${read.reason.replace(/\.?\s*$/, ".")} ${CANVAS_SHARE_DATA_UNREAD}`,
    };
  const digest = read.body["digest"];
  const listed = read.body["dependencies"];
  const dependencies = Array.isArray(listed) ? listed.map(dependencyOf) : null;
  if (
    typeof digest !== "string" ||
    dependencies === null ||
    dependencies.some((item) => item === null)
  )
    return { status: "failed", reason: CANVAS_SHARE_DATA_UNREAD };
  return { status: "loaded", digest, dependencies: dependencies as CanvasShareDependency[] };
}

/** A group of this World by name and size; null when it cannot be read. */
export async function readShareGroup(
  worldKey: string,
  groupId: string,
): Promise<{ readonly name: string; readonly memberCount: number | null } | null> {
  const read = await canvasDoor(worldKey, "group-read", { groupId });
  if (!read.ok) return null;
  const name = read.body["name"];
  const memberCount = read.body["memberCount"];
  return typeof name === "string" && name !== ""
    ? { name, memberCount: Number.isSafeInteger(memberCount) ? (memberCount as number) : null }
    : null;
}
